import { GitCompareArrows } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';

import { Modal } from '@@/modals';
import { Button } from '@@/buttons';

import { EnvironmentReconcileStatus } from '../types';

import { DriftDetails } from './DriftDetails';

type DriftEntry = NonNullable<EnvironmentReconcileStatus['drift']>[number];

/**
 * Shows everything that drifted on one environment.
 *
 * A modal rather than an expanding row: a resource can drift in several places
 * and an environment in several resources, so the patches are long enough to
 * push the rest of the table off the screen when opened inline.
 */
export function DriftModal({
  name,
  drift,
  onDismiss,
}: {
  name: string;
  drift: Array<DriftEntry>;
  onDismiss: () => void;
}) {
  return (
    <Modal onDismiss={onDismiss} aria-label={`Drift on ${name}`} size="lg">
      <Modal.Header title="Drift details" />
      <Modal.Body>
        <p className="text-muted !mb-3 text-xs">
          Each line is a path, then what the source declares → what the cluster
          has. Secrets are masked.
        </p>

        <div className="max-h-96 overflow-y-auto">
          <DriftDetails drift={drift} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onDismiss} color="secondary" data-cy="close-drift">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/** The drift count in the status table, which opens the patches when clicked. */
export function DriftCountButton({
  count,
  onClick,
  index,
}: {
  count: number;
  onClick: () => void;
  index: number;
}) {
  return (
    <Button
      color="link"
      icon={GitCompareArrows}
      onClick={onClick}
      className="!justify-start !p-0 text-left"
      data-cy={`workflow-reconcile-drift-${index}`}
    >
      {count} drifted {pluralize(count, 'resource')}
    </Button>
  );
}
